import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Pipette } from 'lucide-react';
import { Color, colorToHex, hexToColor, rgb, unpackRgb } from '../../core/types';
import { describeColor } from '../../ansi/Palette';

/**
 * Colour picker.
 *
 * Built rather than delegated to `<input type="color">` for one reason that
 * matters here: a terminal design needs to be judged in the mode it will ship
 * in, so the picker shows what the colour becomes in ANSI 256 and ANSI 16 as
 * you drag, and lets you snap to that value. The native dialog cannot.
 */

export interface ColorPickerProps {
  value: Color;
  onChange: (color: Color) => void;
  /** Called when the eyedropper button is pressed. */
  onPickFromCanvas?: () => void;
  compact?: boolean;
}

interface Hsv {
  h: number;
  s: number;
  v: number;
}

export function rgbToHsv(color: Color): Hsv {
  const [r255, g255, b255] = unpackRgb(Math.max(0, color));
  const r = r255 / 255;
  const g = g255 / 255;
  const b = b255 / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToRgb({ h, s, v }: Hsv): Color {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return rgb(Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255));
}

export function ColorPicker({ value, onChange, onPickFromCanvas, compact }: ColorPickerProps): JSX.Element {
  const safe = value < 0 ? 0 : value;
  const [hsv, setHsv] = useState<Hsv>(() => rgbToHsv(safe));
  const [hex, setHex] = useState(() => colorToHex(safe));
  const areaRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'area' | 'hue' | null>(null);

  /*
   * HSV is the editing state because hue survives going fully dark or fully
   * grey, which a round trip through RGB would lose. External changes are
   * adopted only when they differ from what this picker last produced.
   */
  useEffect(() => {
    if (hsvToRgb(hsv) === safe) return;
    setHsv(rgbToHsv(safe));
    setHex(colorToHex(safe));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safe]);

  const emit = useCallback(
    (next: Hsv) => {
      setHsv(next);
      const color = hsvToRgb(next);
      setHex(colorToHex(color));
      onChange(color);
    },
    [onChange],
  );

  const pointToSv = useCallback(
    (clientX: number, clientY: number) => {
      const el = areaRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const s = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      const v = 1 - Math.min(1, Math.max(0, (clientY - r.top) / r.height));
      emit({ ...hsv, s, v });
    },
    [emit, hsv],
  );

  // Dragging continues outside the element, as every picker does.
  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (draggingRef.current === 'area') pointToSv(e.clientX, e.clientY);
    };
    const up = () => {
      draggingRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [pointToSv]);

  const info = describeColor(hsvToRgb(hsv));
  const [r, g, b] = unpackRgb(hsvToRgb(hsv));

  return (
    <div className="picker">
      <div
        ref={areaRef}
        className="picker-area"
        style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${colorToHex(hsvToRgb({ h: hsv.h, s: 1, v: 1 }))})`, height: compact ? 92 : 118 }}
        onPointerDown={(e) => {
          draggingRef.current = 'area';
          e.currentTarget.setPointerCapture(e.pointerId);
          pointToSv(e.clientX, e.clientY);
        }}
      >
        <span
          className="picker-thumb"
          style={{
            left: `${hsv.s * 100}%`,
            top: `${(1 - hsv.v) * 100}%`,
            background: colorToHex(hsvToRgb(hsv)),
          }}
        />
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        <input
          className="picker-hue"
          type="range"
          min={0}
          max={359}
          value={Math.round(hsv.h)}
          onChange={(e) => emit({ ...hsv, h: Number(e.target.value) })}
          aria-label="Hue"
        />
        {onPickFromCanvas ? (
          <button className="btn icon" title="Pick a colour from the canvas" onClick={onPickFromCanvas}>
            <Pipette size={14} />
          </button>
        ) : null}
      </div>

      <div className="row" style={{ marginTop: 8 }}>
        <input
          className="input mono"
          value={hex}
          spellCheck={false}
          onChange={(e) => {
            setHex(e.target.value);
            const parsed = e.target.value.trim().replace(/^#/, '');
            if (/^[0-9a-fA-F]{6}$/.test(parsed) || /^[0-9a-fA-F]{3}$/.test(parsed)) {
              const color = hexToColor(parsed);
              setHsv(rgbToHsv(color));
              onChange(color);
            }
          }}
          onBlur={() => setHex(colorToHex(hsvToRgb(hsv)))}
          aria-label="Hex colour"
        />
      </div>

      {!compact ? (
        <div className="row" style={{ marginTop: 6, gap: 5 }}>
          {(['R', 'G', 'B'] as const).map((label, i) => (
            <label key={label} className="picker-channel">
              <span>{label}</span>
              <input
                className="input mono"
                type="number"
                min={0}
                max={255}
                value={[r, g, b][i]}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(255, Number(e.target.value) || 0));
                  const parts = [r, g, b];
                  parts[i] = v;
                  const color = rgb(parts[0], parts[1], parts[2]);
                  setHsv(rgbToHsv(color));
                  setHex(colorToHex(color));
                  onChange(color);
                }}
              />
            </label>
          ))}
        </div>
      ) : null}

      {/* What this colour becomes in each ANSI mode, and a one-click snap. */}
      <div style={{ marginTop: 8 }}>
        <CompatRow
          label="TrueColor"
          swatch={info.hex}
          value={info.hex}
          exact
          onSnap={null}
        />
        <CompatRow
          label="ANSI 256"
          swatch={info.ansi256Hex}
          value={`${info.ansi256}  ${info.ansi256Hex}`}
          exact={info.exactIn256}
          onSnap={() => {
            const color = hexToColor(info.ansi256Hex);
            setHsv(rgbToHsv(color));
            setHex(colorToHex(color));
            onChange(color);
          }}
        />
        <CompatRow
          label="ANSI 16"
          swatch={info.ansi16Hex}
          value={`${info.ansi16}  ${info.ansi16Name}`}
          exact={info.exactIn16}
          onSnap={() => {
            const color = hexToColor(info.ansi16Hex);
            setHsv(rgbToHsv(color));
            setHex(colorToHex(color));
            onChange(color);
          }}
        />
      </div>
    </div>
  );
}

function CompatRow({
  label,
  swatch,
  value,
  exact,
  onSnap,
}: {
  label: string;
  swatch: string;
  value: string;
  exact: boolean;
  onSnap: (() => void) | null;
}): JSX.Element {
  return (
    <div className="compat-row">
      <span className="chip" style={{ background: swatch }} />
      <span className="faint" style={{ width: 62 }}>
        {label}
      </span>
      <span className="mono dim" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </span>
      {exact ? (
        <Check size={12} className="ok-text" />
      ) : onSnap ? (
        <button
          className="icon-btn"
          style={{ width: 18, height: 18 }}
          title={`Snap to the nearest ${label} colour`}
          onClick={onSnap}
        >
          ≈
        </button>
      ) : null}
    </div>
  );
}
